import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Star, Filter, Search, Navigation, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
// Regional rating data per competitor
const regionalRatings = [{
  region: 'Nord',
  Fielmann: 4.4,
  'Apollo Optik': 3.9,
  'Mister Spex': 4.1,
  'pro optik': 3.8,
  'eyes + more': 4.0,
  markets: 12
}, {
  region: 'Ost',
  Fielmann: 4.2,
  'Apollo Optik': 4.0,
  'Mister Spex': 3.9,
  'pro optik': 3.7,
  'eyes + more': 4.1,
  markets: 9
}, {
  region: 'West',
  Fielmann: 4.1,
  'Apollo Optik': 3.8,
  'Mister Spex': 4.2,
  'pro optik': 3.9,
  'eyes + more': 3.8,
  markets: 15
}, {
  region: 'Mitte',
  Fielmann: 4.35,
  'Apollo Optik': 4.1,
  'Mister Spex': 4.0,
  'pro optik': 3.6,
  'eyes + more': 3.9,
  markets: 8
}, {
  region: 'Sachsen-Thüringen',
  Fielmann: 4.25,
  'Apollo Optik': 3.9,
  'Mister Spex': 4.1,
  'pro optik': 3.8,
  'eyes + more': 4.0,
  markets: 11
}, {
  region: 'Südwest',
  Fielmann: 4.3,
  'Apollo Optik': 4.2,
  'Mister Spex': 4.0,
  'pro optik': 3.7,
  'eyes + more': 3.9,
  markets: 10
}, {
  region: 'Bayern',
  Fielmann: 4.5,
  'Apollo Optik': 4.1,
  'Mister Spex': 4.3,
  'pro optik': 3.9,
  'eyes + more': 4.2,
  markets: 13
}];
// Radius-based mock data
const radiusData: Record<string, {
  name: string;
  type: string;
  rating: number;
  distance: string;
}[]> = {
  'Hamburg, Jungfernstieg': [{
    name: 'Fielmann Hamburg-Hafencity',
    type: 'Fielmann',
    rating: 4.6,
    distance: '0.6 km'
  }, {
    name: 'Apollo Optik Jungfernstieg',
    type: 'Apollo Optik',
    rating: 3.9,
    distance: '0.2 km'
  }, {
    name: 'Mister Spex Hamburg-Mitte',
    type: 'Mister Spex',
    rating: 4.2,
    distance: '0.8 km'
  }, {
    name: 'pro optik Neustadt',
    type: 'pro optik',
    rating: 3.7,
    distance: '1.1 km'
  }, {
    name: 'Fielmann Mönckebergstraße',
    type: 'Fielmann',
    rating: 4.4,
    distance: '1.4 km'
  }],
  'Berlin, Alexanderplatz': [{
    name: 'Fielmann Berlin-Mitte',
    type: 'Fielmann',
    rating: 4.3,
    distance: '0.3 km'
  }, {
    name: 'Apollo Optik Alexanderplatz',
    type: 'Apollo Optik',
    rating: 4.1,
    distance: '0.5 km'
  }, {
    name: 'Mister Spex Alexa',
    type: 'Mister Spex',
    rating: 4.0,
    distance: '0.7 km'
  }, {
    name: 'eyes + more Mitte',
    type: 'eyes + more',
    rating: 3.9,
    distance: '1.2 km'
  }],
  'München, Marienplatz': [{
    name: 'Fielmann München-Schwabing',
    type: 'Fielmann',
    rating: 4.6,
    distance: '0.9 km'
  }, {
    name: 'Apollo Optik Marienplatz',
    type: 'Apollo Optik',
    rating: 4.2,
    distance: '0.4 km'
  }, {
    name: 'Mister Spex München-Zentrum',
    type: 'Mister Spex',
    rating: 4.3,
    distance: '1.3 km'
  }, {
    name: 'pro optik Zentrum',
    type: 'pro optik',
    rating: 3.9,
    distance: '1.1 km'
  }, {
    name: 'Fielmann Kaufingerstraße',
    type: 'Fielmann',
    rating: 4.5,
    distance: '1.8 km'
  }]
};
const regions = ['Alle Regionen', 'Nord', 'Ost', 'West', 'Mitte', 'Sachsen-Thüringen', 'Südwest', 'Bayern'];
const radiusOptions = ['1 km', '2 km', '5 km', '10 km'];
const competitors = ['Fielmann', 'Apollo Optik', 'Mister Spex', 'pro optik', 'eyes + more'];
const competitorBarColors: Record<string, string> = {
  Fielmann: '#1a1a2e',
  'Apollo Optik': '#005aaa',
  'Mister Spex': '#515151',
  'pro optik': '#eb5e32',
  'eyes + more': '#f28902'
};
const competitorLogos: Record<string, string> = {
  Fielmann: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Fielmann_Group_AG_Logo.svg/250px-Fielmann_Group_AG_Logo.svg.png',
  'Apollo Optik': 'https://upload.wikimedia.org/wikipedia/de/thumb/7/7b/Apollo-Optik_logo.svg/330px-Apollo-Optik_logo.svg.png',
  'Mister Spex': 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/46/MSX-Logo-ohne-Claim_black.png/330px-MSX-Logo-ohne-Claim_black.png',
  'pro optik': 'https://www.prooptik.de/public/Marketing/Website/4736/image-thumb__4736___auto_40cd750bba9870f18aada2478b24840a/logo-optik.png',
  'eyes + more': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6e/Eyes_and_more_Logo.svg/330px-Eyes_and_more_Logo.svg.png'
};
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string, isRating: boolean = false): number {
  if (period === 'gesamt' || value === 0) return value;
  if (isRating) {
    const noise = period === '30' ? 0.1 : period === '60' ? -0.1 : period === '120' ? 0.05 : -0.05;
    return Math.max(1, Math.min(5, value + noise));
  }
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
export function CompetitorRadarPage() {
  const [selectedRegion, setSelectedRegion] = useState('Alle Regionen');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [addressInput, setAddressInput] = useState('');
  const [selectedRadius, setSelectedRadius] = useState('5 km');
  const [addressSearched, setAddressSearched] = useState('Hamburg, Jungfernstieg');
  const [hasSearched, setHasSearched] = useState(true);
  const rawFilteredRegionalData = selectedRegion === 'Alle Regionen' ? regionalRatings : regionalRatings.filter(r => r.region === selectedRegion);
  const filteredRegionalData = rawFilteredRegionalData.map(r => ({
    ...r,
    Fielmann: applyTimeFactor(r['Fielmann'], selectedPeriod, true),
    'Apollo Optik': applyTimeFactor(r['Apollo Optik'], selectedPeriod, true),
    'Mister Spex': applyTimeFactor(r['Mister Spex'], selectedPeriod, true),
    'pro optik': r['pro optik'] > 0 ? applyTimeFactor(r['pro optik'], selectedPeriod, true) : 0,
    'eyes + more': r['eyes + more'] > 0 ? applyTimeFactor(r['eyes + more'], selectedPeriod, true) : 0,
    markets: applyTimeFactor(r.markets, selectedPeriod)
  }));
  const regionBarData = selectedRegion === 'Alle Regionen' ? (() => {
    const avg = (key: string) => {
      const vals = filteredRegionalData.map(r => (r as any)[key]).filter((v: number) => v > 0);
      return vals.length > 0 ? vals.reduce((a: number, b: number) => a + b, 0) / vals.length : null;
    };
    return [{
      region: 'Gesamt',
      Fielmann: avg('Fielmann'),
      'Apollo Optik': avg('Apollo Optik'),
      'Mister Spex': avg('Mister Spex'),
      'pro optik': avg('pro optik'),
      'eyes + more': avg('eyes + more')
    }];
  })() : filteredRegionalData.map(r => ({
    region: r.region,
    Fielmann: r['Fielmann'],
    'Apollo Optik': r['Apollo Optik'],
    'Mister Spex': r['Mister Spex'],
    'pro optik': r['pro optik'] || null,
    'eyes + more': r['eyes + more'] || null
  }));
  const renderBarLogo = (comp: string) => (props: any) => {
    const {
      x,
      y,
      width,
      height,
      value
    } = props;
    if (!value || value === 0 || width < 10) return null;
    const logoSize = 80;
    return <image href={competitorLogos[comp]} x={x + (width - logoSize) / 2} y={y - logoSize - 6} width={logoSize} height={logoSize} preserveAspectRatio="xMidYMid meet" data-id="element-330" />;
  };
  const rawRadiusStores = radiusData[addressSearched] || radiusData['Hamburg, Jungfernstieg'];
  const radiusStores = rawRadiusStores.map(s => ({
    ...s,
    rating: applyTimeFactor(s.rating, selectedPeriod, true)
  }));
  const radiusAggregated = competitors.map(type => {
    const stores = radiusStores.filter(s => s.type === type);
    const avg = stores.length > 0 ? stores.reduce((acc, s) => acc + s.rating, 0) / stores.length : null;
    return {
      name: type,
      rating: avg,
      count: stores.length
    };
  }).filter(d => d.rating !== null);
  const handleSearch = () => {
    const key = Object.keys(radiusData).find(k => k.toLowerCase().includes(addressInput.toLowerCase().split(',')[0].toLowerCase())) || 'Hamburg, Jungfernstieg';
    setAddressSearched(key);
    setHasSearched(true);
  };
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-331">
      <Card data-id="element-332">
        <div className="p-6" data-id="element-333">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-4" data-id="element-334">
            <div data-id="element-335">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-336">
                Regionaler Bewertungsvergleich
              </h3>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-337">
                Durchschnittliche Google-Bewertungen nach Region
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 mb-5" data-id="element-338">
            <div className="flex items-center gap-3 flex-wrap" data-id="element-339">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600" data-id="element-340">
                <Filter size={14} data-id="element-341" />
                <span data-id="element-342">Region:</span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-343">
                {regions.map(region => <button key={region} onClick={() => setSelectedRegion(region)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedRegion === region ? 'bg-edeka-blue text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-344">
                    {region}
                  </button>)}
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap" data-id="element-345">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600" data-id="element-346">
                <Calendar size={14} data-id="element-347" />
                <span data-id="element-348">Zeitraum:</span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-349">
                {timePeriods.map(period => <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedPeriod === period.id ? 'bg-edeka-blue text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-350">
                    {period.label}
                  </button>)}
              </div>
            </div>
          </div>

          <div className="h-96" data-id="element-351">
            <ResponsiveContainer width="100%" height="100%" data-id="element-352">
              <BarChart data={regionBarData} margin={{
              top: 50,
              right: 20,
              left: 0,
              bottom: 5
            }} barCategoryGap={selectedRegion === 'Alle Regionen' ? '15%' : '12%'} barGap={selectedRegion === 'Alle Regionen' ? 3 : 1} data-id="element-353">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-354" />
                <XAxis dataKey="region" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6b7280'
              }} data-id="element-355" />
                <YAxis domain={[3, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={v => v.toFixed(1) + ' ★'} data-id="element-356" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [value.toFixed(1) + ' ★', name]} data-id="element-357" />
                {competitors.map(comp => <Bar key={comp} dataKey={comp} name={comp} fill={competitorBarColors[comp]} radius={[3, 3, 0, 0]} fillOpacity={comp === 'Fielmann' ? 1 : 0.55} label={renderBarLogo(comp)} data-id="element-358" />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <Card data-id="element-359">
        <div className="p-6" data-id="element-360">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-4" data-id="element-361">
            <div data-id="element-362">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-363">
                Lokale Umkreissuche
              </h3>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-364">
                Bewertungen im direkten Umfeld vergleichen
              </p>
            </div>
          </div>

          <div className="flex gap-3 mb-5 flex-wrap" data-id="element-365">
            <div className="flex-1 min-w-48 relative" data-id="element-366">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-367" />
              <input type="text" placeholder="Adresse eingeben, z.B. Hamburg, Jungfernstieg" value={addressInput} onChange={e => setAddressInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-edeka-blue/20 focus:border-edeka-blue" data-id="element-368" />
            </div>
            <div className="flex items-center gap-2" data-id="element-369">
              <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-370">
                Radius:
              </span>
              <div className="flex gap-1" data-id="element-371">
                {radiusOptions.map(r => <button key={r} onClick={() => setSelectedRadius(r)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${selectedRadius === r ? 'bg-edeka-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-372">
                    {r}
                  </button>)}
              </div>
            </div>
            <button onClick={handleSearch} className="flex items-center gap-2 px-4 py-2.5 bg-edeka-blue text-white rounded-lg text-sm font-medium hover:bg-edeka-blue/90 transition-colors" data-id="element-373">
              <Search size={14} data-id="element-374" />
              Suchen
            </button>
          </div>

          {hasSearched && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} data-id="element-375">
              <div className="text-xs text-gray-500 mb-4 flex items-center gap-1.5" data-id="element-376">
                <Navigation size={12} data-id="element-377" />
                <span data-id="element-378">
                  Ergebnisse für{' '}
                  <strong className="text-gray-700" data-id="element-379">{addressSearched}</strong>{' '}
                  im Umkreis von{' '}
                  <strong className="text-gray-700" data-id="element-380">{selectedRadius}</strong> —{' '}
                  {radiusStores.length} Filialen gefunden
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-381">
                <div data-id="element-382">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-383">
                    Ø Bewertung nach Kette
                  </div>
                  <div className="space-y-3" data-id="element-384">
                    {radiusAggregated.sort((a, b) => (b.rating || 0) - (a.rating || 0)).map((item, i) => <div key={item.name} className="flex items-center gap-3" data-id="element-385">
                          <img src={competitorLogos[item.name]} alt={item.name} className="h-5 w-5 object-contain shrink-0 bg-white rounded" data-id="element-386" />
                          <div className="w-24 text-sm font-medium text-gray-700" data-id="element-387">
                            {item.name}
                          </div>
                          <div className="flex-1 h-7 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-388">
                            <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${(item.rating || 0) / 5 * 100}%`
                    }} transition={{
                      duration: 0.5,
                      delay: i * 0.08
                    }} className="h-full rounded-full" style={{
                      backgroundColor: competitorBarColors[item.name],
                      opacity: item.name === 'Fielmann' ? 1 : 0.6
                    }} data-id="element-389" />
                          </div>
                          <div className="flex items-center gap-1 w-16 text-right" data-id="element-390">
                            <span className={`text-sm font-bold ${item.name === 'Fielmann' ? 'text-edeka-blue' : 'text-gray-600'}`} data-id="element-391">
                              {item.rating?.toFixed(1)}
                            </span>
                            <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-392" />
                          </div>
                          <div className="text-xs text-gray-400 w-16" data-id="element-393">
                            {item.count} Filialen
                          </div>
                        </div>)}
                  </div>
                </div>

                <div data-id="element-394">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-395">
                    Einzelne Filialen im Umkreis
                  </div>
                  <div className="space-y-2" data-id="element-396">
                    {radiusStores.map((store, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  x: 10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: i * 0.06
                }} className={`flex items-center justify-between p-2.5 rounded-lg border ${store.type === 'Fielmann' ? 'border-edeka-blue/20 bg-edeka-blue/5' : 'border-gray-100 bg-gray-50'}`} data-id="element-397">
                        <div className="flex items-center gap-2 min-w-0" data-id="element-398">
                          <img src={competitorLogos[store.type]} alt={store.type} className="h-4 w-4 object-contain shrink-0 bg-white rounded" data-id="element-399" />
                          <span className={`text-sm truncate ${store.type === 'Fielmann' ? 'font-medium text-gray-900' : 'text-gray-600'}`} data-id="element-400">
                            {store.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-2" data-id="element-401">
                          <span className="text-xs text-gray-400" data-id="element-402">
                            {store.distance}
                          </span>
                          <div className="flex items-center gap-1" data-id="element-403">
                            <span className={`text-sm font-semibold ${store.type === 'Fielmann' ? 'text-edeka-blue' : 'text-gray-600'}`} data-id="element-404">
                              {store.rating.toFixed(1)}
                            </span>
                            <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-405" />
                          </div>
                        </div>
                      </motion.div>)}
                  </div>
                </div>
              </div>
            </motion.div>}
        </div>
      </Card>
    </div>;
}